const SEP = ' - ';

/**
 * Flattens nested objects and arrays into a single level of string columns for Excel export.
 * Nested object keys use " - " between parent and child; array elements use "[n]" in the path.
 */
export function flattenValue(value: unknown, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  if (value === null || value === undefined) {
    if (prefix) {
      out[prefix] = '';
    }
    return out;
  }
  if (typeof value !== 'object') {
    out[prefix || 'value'] = String(value);
    return out;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      out[prefix || 'items'] = '';
      return out;
    }
    const allPrimitive = value.every(
      item =>
        item === null ||
        item === undefined ||
        ['string', 'number', 'boolean'].includes(typeof item)
    );
    if (allPrimitive) {
      out[prefix || 'items'] = value.map(v => String(v)).join('; ');
    } else {
      value.forEach((item, i) => {
        const p = prefix ? `${prefix}${SEP}[${i}]` : `[${i}]`;
        Object.assign(out, flattenValue(item, p));
      });
    }
    return out;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const key = prefix ? `${prefix}${SEP}${k}` : k;
    if (v === null || v === undefined) {
      out[key] = '';
    } else if (Array.isArray(v)) {
      Object.assign(out, flattenValue(v, key));
    } else if (typeof v === 'object') {
      Object.assign(out, flattenValue(v, key));
    } else {
      out[key] = String(v);
    }
  }
  return out;
}

export function flattenRecordsForExcel(rows: unknown[]): {
  flatRows: Record<string, string>[];
  columnOrder: string[];
} {
  if (!rows?.length) {
    return {flatRows: [], columnOrder: []};
  }
  const flatRows = rows.map(row => flattenValue(row));
  const keySet = new Set<string>();
  flatRows.forEach(r => {
    Object.keys(r).forEach(k => keySet.add(k));
  });
  const columnOrder = Array.from(keySet);
  return {flatRows, columnOrder};
}
